import React, { useEffect, useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { sr } from "../../lib/sr";

const LABELS = { not_started: "Not started", testing: "Testing", worked: "Worked", didnt_work: "Didn't work", paused: "Paused" };
const TONE = { not_started: "sr-badge-gray", testing: "sr-badge-blue", worked: "sr-badge-green", didnt_work: "sr-badge-red", paused: "sr-badge-amber" };
const inputStyle = { padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit" };

export default function StrategyTracker() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const load = () => sr.strategyTracker().then(setData).catch(() => setData({ applies: false }));
  useEffect(() => { load(); }, []);
  if (!data || !data.applies) return null;

  const setField = (key, patch) => setData((d) => ({ ...d, strategies: d.strategies.map((s) => s.key === key ? { ...s, ...patch } : s) }));
  const save = async (s) => {
    setSaving(s.key);
    try { await sr.saveStrategyValidation({ strategy_key: s.key, status: s.status, result: s.result, notes: s.notes }); await load(); }
    finally { setSaving(null); }
  };

  return (
    <div className="sr-card" data-testid="strategy-tracker" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <ClipboardCheck size={18} color="var(--blue)" />
        <h3 style={{ fontSize: 16, margin: 0 }}>Candidate Strategy Tracker</h3>
        <span className="sr-badge sr-badge-green" data-testid="strategy-summary" style={{ marginLeft: "auto", fontSize: 11 }}>
          {data.summary.worked} worked · {data.summary.validated}/{data.summary.total} validated
        </span>
      </div>
      <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>Log how each candidate strategy performed so you can see which honest approaches actually work.</p>

      {data.strategies.map((s) => (
        <div key={s.key} data-testid={`strategy-${s.key}`} style={{ borderTop: "1px solid #f1f5f9", paddingTop: 10, marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <strong style={{ fontSize: 13 }}>{s.label}</strong>
            <span className={`sr-badge ${TONE[s.status]}`} style={{ fontSize: 10 }} data-testid={`strategy-${s.key}-status`}>{LABELS[s.status]}</span>
          </div>
          <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>{s.approach}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select data-testid={`strategy-${s.key}-select`} value={s.status} onChange={(e) => setField(s.key, { status: e.target.value })} style={inputStyle}>
              {data.statuses.map((st) => <option key={st} value={st}>{LABELS[st]}</option>)}
            </select>
            <input data-testid={`strategy-${s.key}-result`} value={s.result || ""} onChange={(e) => setField(s.key, { result: e.target.value })} placeholder="Result (e.g. CPA $32, 48 leads)" style={{ ...inputStyle, flex: 1, minWidth: 160 }} />
            <button className="sr-btn sr-btn-primary" data-testid={`strategy-${s.key}-save`} onClick={() => save(s)} disabled={saving === s.key}>
              {saving === s.key ? <Loader2 size={14} className="sr-spin" /> : "Save"}
            </button>
          </div>
          <textarea data-testid={`strategy-${s.key}-notes`} value={s.notes || ""} onChange={(e) => setField(s.key, { notes: e.target.value })} placeholder="Notes…" rows={2} style={{ ...inputStyle, width: "100%", marginTop: 6 }} />
        </div>
      ))}
    </div>
  );
}
